import { useCallback, useEffect, useRef, useState } from 'react'
import { playAudioAndCatchAbortError } from 'utils'
import { TPlayState } from 'containers/SubSidebar/AiAvatarContent/sections/SpeechSection/types'

// Plays the attached voice file itself (local blob: or My Files url) so the
// user can listen back to what they uploaded. Unlike `useTrialAudio` there is
// no generation step — the audio element just streams the given url.
export const useVoicePreview = (url: string) => {
  const [playState, setPlayState] = useState<TPlayState>('idle')
  const [progress, setProgress] = useState(0)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const getAudio = useCallback(() => {
    if (!audioRef.current) {
      const audio = new Audio()
      audio.onplaying = () => setPlayState('playing')
      audio.onended = () => {
        setPlayState('idle')
        setProgress(0)
      }
      audio.ontimeupdate = () => {
        if (audio.duration) {
          setProgress(audio.currentTime / audio.duration)
        }
      }
      audio.onerror = () => {
        setPlayState('idle')
        setProgress(0)
      }
      audioRef.current = audio
    }
    return audioRef.current
  }, [])

  const stop = useCallback(() => {
    const audio = audioRef.current
    if (audio) {
      audio.pause()
      audio.currentTime = 0
    }
    setPlayState('idle')
    setProgress(0)
  }, [])

  const toggle = useCallback(() => {
    if (playState !== 'idle') {
      stop()
      return
    }

    const audio = getAudio()
    if (audio.src !== url) {
      audio.src = url
    }
    audio.currentTime = 0
    setProgress(0)
    setPlayState('loading')
    playAudioAndCatchAbortError(audio)
  }, [playState, url, getAudio, stop])

  // A different voice was attached — stop any playback of the previous one.
  useEffect(() => {
    stop()
  }, [url, stop])

  // Full teardown on unmount so nothing keeps playing after the card is gone.
  useEffect(() => {
    return () => {
      const audio = audioRef.current
      if (audio) {
        audio.pause()
        audio.src = ''
        audio.onplaying = null
        audio.onended = null
        audio.ontimeupdate = null
        audio.onerror = null
      }
    }
  }, [])

  return { playState, progress, toggle }
}
